
import Fab from '@mui/material/Fab'
import AppBar from '@mui/material/AppBar'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import MuiToolbar from '@mui/material/Toolbar'
import Icon from 'src/@core/components/icon'
import themeConfig from 'src/configs/themeConfig'
import Customizer from 'src/@core/components/customizer'
import ScrollToTop from 'src/@core/components/scroll-to-top'
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { useAuth } from 'src/hooks/useAuth'

import Footer from './components/shared-components/footer'
import Navigation from './components/horizontal/navigation'
import AppBarContent from './components/horizontal/app-bar-content'

const HorizontalLayoutWrapper = styled('div')({
  minHeight: '100vh',
  display: 'flex'
})

const MainContentWrapper = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  minWidth: 0,
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default
}))

const Toolbar = styled(MuiToolbar)(({ theme }) => ({
  width: '100%',
  padding: `${theme.spacing(0, 6, 0, 0)} !important`,
  [theme.breakpoints.down('sm')]: {
    paddingRight: theme.spacing(4)
  },
  [theme.breakpoints.down('xs')]: {
    paddingRight: theme.spacing(2)
  }
}))

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  padding: theme.spacing(6),
  transition: 'padding .25s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4)
  }
}))

const HorizontalLayout = props => {

  const {
    hidden,
    children,
    settings,
    scrollToTop,
    footerProps,
    saveSettings,
    contentHeightFixed,
    horizontalLayoutProps
  } = props

  const { skin, appBar, navHidden, appBarBlur, contentWidth } = settings

  const auth = useAuth()
  const isCampaigner = auth.user?.role === 'campaigner'

  const appBarProps = horizontalLayoutProps?.appBar?.componentProps

  const userNavMenuContent = horizontalLayoutProps?.navMenu?.content

  let userAppBarStyle = {}

  if (appBarProps && appBarProps.sx) {
    userAppBarStyle = appBarProps.sx
  }
  const userAppBarProps = Object.assign({}, appBarProps)

  delete userAppBarProps.sx

  return (
    <HorizontalLayoutWrapper className='layout-wrapper'>
      <MainContentWrapper className='layout-content-wrapper'>
 
        <AppBar
          color='default'
          elevation={skin === 'bordered' ? 0 : 3}
          className='layout-navbar-and-nav-container'
          position={appBar === 'fixed' ? 'sticky' : 'static'}
          sx={{
            alignItems: 'center',
            color: 'text.primary',
            justifyContent: 'center',
            backgroundColor: 'background.paper',
            ...(appBar === 'static' && { zIndex: 13 }),
            ...(skin === 'bordered' && { borderBottom: theme => `1px solid ${theme.palette.divider}` }),
            transition: 'border-bottom 0.2s ease-in-out, backdrop-filter .25s ease-in-out, box-shadow .25s ease-in-out',
            ...(appBar === 'fixed'
              ? appBarBlur && {
                backdropFilter: 'blur(8px)',
                backgroundColor: theme => hexToRGBA(theme.palette.background.paper, 0.9)
              }
              : {}),
            ...userAppBarStyle
          }}
          {...userAppBarProps}
        >
         
          <Box
            className='layout-navbar'
            sx={{
              backgroundColor: '#1B205C',
              width: '100%',
              ...(navHidden ? {} : { borderBottom: theme => `1px solid ${theme.palette.divider}` })
            }}
          >
            <Toolbar
              className='navbar-content-container'
              sx={{
                mx: 'auto',
                ...(contentWidth === 'boxed'),
                // Fixed height on purpose - shrinking the header on scroll moved the
                // page under it, scroll anchoring moved it back across the threshold,
                // and the header flickered between both heights until scrolled past.
                minHeight: '52px !important'
              }}
            >
              <AppBarContent
                {...props}
                hidden={hidden}
                settings={settings}
                saveSettings={saveSettings}
                appBarContent={horizontalLayoutProps?.appBar?.content}
                appBarBranding={horizontalLayoutProps?.appBar?.branding}
                navigationContent={
                  navHidden
                    ? null
                    : (userNavMenuContent && userNavMenuContent(props)) || (
                      <Navigation
                        {...props}
                        horizontalNavItems={
                          isCampaigner
                            ? horizontalLayoutProps.navMenu?.navItems.filter(item => item.title !== 'Administration')
                            : horizontalLayoutProps.navMenu?.navItems
                        }
                      />
                    )
                }
              />
            </Toolbar>
          </Box>
        </AppBar>

        <ContentWrapper
          className='layout-page-content'
          sx={{
            ...(contentHeightFixed && { display: 'flex', overflow: 'hidden' }),
            ...(contentWidth === 'boxed'
            )
          }}
        >
          {children}
        </ContentWrapper>

        <Footer {...props} footerStyles={footerProps?.sx} footerContent={footerProps?.content} />

        {themeConfig.disableCustomizer || hidden ? null : <Customizer />}

        {scrollToTop ? (
          scrollToTop(props)
        ) : (
          <ScrollToTop className='mui-fixed'>
            <Fab color='primary' size='small' aria-label='scroll back to top'>
              <Icon icon='mdi:arrow-up' />
            </Fab>
          </ScrollToTop>
        )}
      </MainContentWrapper>
    </HorizontalLayoutWrapper>
  )
}

export default HorizontalLayout
